import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft, Edit2, Check, X, Car, Mail, Phone, MapPin, ChevronRight,
  Plus, UserCheck, UserX, Link,
} from 'lucide-react'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import {
  useCustomer, useUpdateCustomer, useJobs,
  useAddVehicle, useLinkCustomerUser,
} from '../api/hooks'
import { useAuth } from '../context/AuthContext'

const JOB_STATUS_STYLES = {
  quoted:            'bg-purple-500/15 text-purple-400',
  awaiting_approval: 'bg-yellow-500/15 text-yellow-400',
  approved:          'bg-indigo-500/15 text-indigo-400',
  in_progress:       'bg-amber-500/15 text-amber-400',
  waiting_on_parts:  'bg-orange-500/15 text-orange-400',
  completed:         'bg-green-500/15 text-green-400',
  cancelled:         'bg-gray-500/15 text-gray-400',
}

const JOB_STATUS_LABELS = {
  quoted: 'Quoted', awaiting_approval: 'Awaiting', approved: 'Approved',
  in_progress: 'In Progress', waiting_on_parts: 'Waiting on Parts',
  completed: 'Completed', cancelled: 'Cancelled',
}

const FUEL_OPTIONS  = ['petrol', 'diesel', 'electric', 'hybrid', 'other']
const TRANS_OPTIONS = ['manual', 'automatic', 'other']

// ─── Shared ───────────────────────────────────────────────────────────────────

function Plate({ registration }) {
  return (
    <span className="inline-block font-mono font-bold tracking-widest text-xs
                     bg-yellow-400 text-black px-2 py-0.5 rounded">
      {registration}
    </span>
  )
}

const inputCls = `w-full px-3 py-2 text-sm bg-[var(--bg-base)] border border-[var(--border)]
  rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)]
  focus:outline-none focus:border-indigo-500 transition-colors`

// ─── Vehicle mini-card ────────────────────────────────────────────────────────

function VehicleMiniCard({ vehicle, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 p-3 bg-[var(--bg-base)] border border-[var(--border)]
                 rounded-lg hover:border-indigo-500/50 transition-colors text-left group"
    >
      <div className="w-8 h-8 rounded-lg bg-indigo-600/10 flex items-center justify-center shrink-0">
        <Car size={15} className="text-indigo-400" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <Plate registration={vehicle.registration} />
          <span className="text-sm font-medium text-[var(--text-primary)] group-hover:text-indigo-400 transition-colors">
            {vehicle.make} {vehicle.model}
          </span>
        </div>
        <p className="text-xs text-[var(--text-secondary)] mt-0.5">
          {vehicle.year || ''}
          {vehicle.colour ? ` · ${vehicle.colour}` : ''}
          {vehicle.currentMileage
            ? ` · ${Number(vehicle.currentMileage).toLocaleString()} miles`
            : ''}
        </p>
      </div>
      <ChevronRight size={14} className="shrink-0 text-[var(--text-muted)] group-hover:text-indigo-400" />
    </button>
  )
}

// ─── Contact row ──────────────────────────────────────────────────────────────

function ContactItem({ icon: Icon, value, href }) {
  if (!value) return null
  return (
    <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
      <Icon size={14} className="text-[var(--text-muted)] shrink-0" />
      {href ? (
        <a href={href} className="hover:text-indigo-400 transition-colors truncate">{value}</a>
      ) : (
        <span className="truncate">{value}</span>
      )}
    </div>
  )
}

// ─── Add Vehicle Modal ────────────────────────────────────────────────────────

function AddVehicleModal({ customer, onClose }) {
  const qc = useQueryClient()
  const addVehicle = useAddVehicle()

  const [form, setForm] = useState({
    registration: '', make: '', model: '', year: '', colour: '',
    engineSize: '', fuelType: '', transmission: '',
  })

  const set = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.registration.trim() || !form.make.trim() || !form.model.trim()) {
      toast.error('Registration, make and model are required')
      return
    }
    try {
      await addVehicle.mutateAsync({
        ...form,
        primaryGarageId: customer.garage_id,
        customerId: customer.id,
      })
      qc.invalidateQueries({ queryKey: ['customer', customer.id] })
      toast.success('Vehicle added')
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add vehicle')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)]">
          <h2 className="font-semibold text-[var(--text-primary)]">Add Vehicle</h2>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs text-[var(--text-muted)] mb-1">Registration *</label>
              <input
                type="text"
                value={form.registration}
                onChange={set('registration')}
                placeholder="e.g. AB12 CDE"
                className={inputCls}
                required
              />
            </div>
            {[
              { label: 'Make *',   field: 'make',       placeholder: 'e.g. Ford'    },
              { label: 'Model *',  field: 'model',      placeholder: 'e.g. Focus'   },
              { label: 'Year',     field: 'year',       placeholder: 'e.g. 2019'    },
              { label: 'Colour',   field: 'colour',     placeholder: 'e.g. White'   },
              { label: 'Engine',   field: 'engineSize', placeholder: 'e.g. 2.0L'    },
            ].map(({ label, field, placeholder }) => (
              <div key={field}>
                <label className="block text-xs text-[var(--text-muted)] mb-1">{label}</label>
                <input
                  type={field === 'year' ? 'number' : 'text'}
                  value={form[field]}
                  onChange={set(field)}
                  placeholder={placeholder}
                  className={inputCls}
                />
              </div>
            ))}
            <div>
              <label className="block text-xs text-[var(--text-muted)] mb-1">Fuel type</label>
              <select value={form.fuelType} onChange={set('fuelType')} className={inputCls}>
                <option value="">—</option>
                {FUEL_OPTIONS.map(f => (
                  <option key={f} value={f}>{f.charAt(0).toUpperCase() + f.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-[var(--text-muted)] mb-1">Transmission</label>
              <select value={form.transmission} onChange={set('transmission')} className={inputCls}>
                <option value="">—</option>
                {TRANS_OPTIONS.map(t => (
                  <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                         text-[var(--text-secondary)] rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={addVehicle.isPending}
              className="px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50
                         text-white font-medium rounded-lg transition-colors"
            >
              {addVehicle.isPending ? 'Adding…' : 'Add Vehicle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function CustomerDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data: customer, isLoading, error } = useCustomer(id)
  const updateCustomer = useUpdateCustomer()
  const linkUser = useLinkCustomerUser()
  const { data: jobs = [], isLoading: jobsLoading } = useJobs({ customerId: id })

  const [editing,        setEditing]        = useState(false)
  const [form,           setForm]           = useState(null)
  const [showAddVehicle, setShowAddVehicle] = useState(false)
  const [linkEmail,      setLinkEmail]      = useState('')
  const [showLinkForm,   setShowLinkForm]   = useState(false)

  const isAdmin = user?.role === 'garage_admin' || user?.role === 'platform_admin'

  const startEdit = () => {
    setForm({
      name:    customer.name    || '',
      email:   customer.email   || '',
      phone:   customer.phone   || '',
      address: customer.address || '',
    })
    setEditing(true)
  }

  const set = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }))

  const handleSave = async () => {
    try {
      await updateCustomer.mutateAsync({ id, ...form })
      toast.success('Customer updated')
      setEditing(false)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Update failed')
    }
  }

  const handleLinkUser = async (e) => {
    e.preventDefault()
    if (!linkEmail.trim()) return
    try {
      await linkUser.mutateAsync({ customerId: id, email: linkEmail.trim() })
      toast.success('Portal account linked')
      setShowLinkForm(false)
      setLinkEmail('')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Link failed')
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-32 bg-[var(--bg-surface)] rounded-lg" />
        <div className="h-48 bg-[var(--bg-surface)] rounded-xl" />
      </div>
    )
  }

  if (error || !customer) {
    return (
      <div className="space-y-4">
        <button onClick={() => navigate('/garage/customers')}
          className="flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
          <ArrowLeft size={16} /> Customers
        </button>
        <p className="text-[var(--text-secondary)]">Customer not found.</p>
      </div>
    )
  }

  const vehicles = customer.vehicles || []

  return (
    <div className="space-y-6 pb-12 max-w-2xl">
      {showAddVehicle && (
        <AddVehicleModal customer={customer} onClose={() => setShowAddVehicle(false)} />
      )}

      {/* Back */}
      <button
        onClick={() => navigate('/garage/customers')}
        className="flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
      >
        <ArrowLeft size={16} />
        Customers
      </button>

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-indigo-600/15 flex items-center justify-center shrink-0">
            <span className="font-display font-bold text-lg text-indigo-400">
              {customer.name[0]?.toUpperCase()}
            </span>
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold text-[var(--text-primary)]">
              {customer.name}
            </h1>
            <p className="text-sm text-[var(--text-secondary)]">
              {vehicles.length} {vehicles.length === 1 ? 'vehicle' : 'vehicles'}
            </p>
          </div>
        </div>
        {isAdmin && !editing && (
          <button onClick={startEdit}
            className="flex items-center gap-1.5 px-3 py-2 bg-[var(--bg-surface)] border border-[var(--border)]
                       hover:border-[var(--border-strong)] text-[var(--text-secondary)] text-sm font-medium
                       rounded-lg transition-colors shrink-0">
            <Edit2 size={14} />
            Edit
          </button>
        )}
      </div>

      {/* Contact details */}
      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-[var(--text-primary)]">Contact Details</h3>
          {editing && (
            <div className="flex gap-2">
              <button onClick={handleSave} disabled={updateCustomer.isPending}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50
                           text-white text-xs font-medium rounded-lg transition-colors">
                <Check size={14} /> Save
              </button>
              <button onClick={() => setEditing(false)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                           text-[var(--text-secondary)] text-xs font-medium rounded-lg transition-colors">
                <X size={14} /> Cancel
              </button>
            </div>
          )}
        </div>

        {editing ? (
          <div className="space-y-3">
            {[
              { label: 'Name',    field: 'name',    type: 'text',  required: true },
              { label: 'Email',   field: 'email',   type: 'email'  },
              { label: 'Phone',   field: 'phone',   type: 'tel'    },
              { label: 'Address', field: 'address', type: 'text'   },
            ].map(({ label, field, type, required }) => (
              <div key={field}>
                <label className="block text-xs text-[var(--text-muted)] mb-1">{label}</label>
                <input type={type} value={form[field]} onChange={set(field)} required={required} className={inputCls} />
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-2.5">
            <ContactItem icon={Mail}  value={customer.email}   href={`mailto:${customer.email}`} />
            <ContactItem icon={Phone} value={customer.phone}   href={`tel:${customer.phone}`} />
            <ContactItem icon={MapPin} value={customer.address} />
            {!customer.email && !customer.phone && !customer.address && (
              <p className="text-sm text-[var(--text-muted)]">No contact details recorded.</p>
            )}
          </div>
        )}
      </div>

      {/* Portal account */}
      {isAdmin && (
        <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-[var(--text-primary)]">Portal Account</h3>
          </div>

          {customer.user_id ? (
            <div className="flex items-center gap-2 text-sm text-green-400">
              <UserCheck size={15} />
              <span>Linked to <strong>{customer.linked_user_name || customer.linked_user_email || customer.user_id}</strong></span>
              {customer.linked_user_email && customer.linked_user_name && (
                <span className="text-[var(--text-muted)] text-xs">({customer.linked_user_email})</span>
              )}
            </div>
          ) : showLinkForm ? (
            <form onSubmit={handleLinkUser} className="flex items-center gap-2">
              <input
                type="email"
                value={linkEmail}
                onChange={e => setLinkEmail(e.target.value)}
                placeholder="User's email address"
                className="flex-1 px-3 py-1.5 text-sm bg-[var(--bg-base)] border border-[var(--border)]
                           rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)]
                           focus:outline-none focus:border-indigo-500 transition-colors"
                required
              />
              <button
                type="submit"
                disabled={linkUser.isPending}
                className="px-3 py-1.5 text-xs bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50
                           text-white font-medium rounded-lg transition-colors whitespace-nowrap"
              >
                {linkUser.isPending ? 'Linking…' : 'Link'}
              </button>
              <button
                type="button"
                onClick={() => { setShowLinkForm(false); setLinkEmail('') }}
                className="p-1.5 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              >
                <X size={15} />
              </button>
            </form>
          ) : (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 text-sm text-[var(--text-muted)]">
                <UserX size={14} />
                <span>No portal account linked</span>
              </div>
              <button
                onClick={() => setShowLinkForm(true)}
                className="flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
              >
                <Link size={12} />
                Link account
              </button>
            </div>
          )}
        </div>
      )}

      {/* Vehicles */}
      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-[var(--text-primary)]">Vehicles</h3>
          {isAdmin && (
            <button
              onClick={() => setShowAddVehicle(true)}
              className="flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
            >
              <Plus size={13} />
              Add vehicle
            </button>
          )}
        </div>
        {vehicles.length === 0 ? (
          <p className="text-sm text-[var(--text-muted)]">
            No vehicles linked.
            {!customer.user_id && !isAdmin && (
              <span className="block mt-1 text-xs">
                This customer does not have a portal account. Vehicles will appear here once they register and link this garage.
              </span>
            )}
          </p>
        ) : (
          <div className="space-y-2">
            {vehicles.map(v => (
              <VehicleMiniCard
                key={v.id}
                vehicle={v}
                onClick={() => navigate(`/garage/vehicles/${v.id}`)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Job history */}
      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)]">
          <h3 className="font-medium text-[var(--text-primary)]">
            Jobs {!jobsLoading && jobs.length > 0 && (
              <span className="text-[var(--text-muted)] font-normal">({jobs.length})</span>
            )}
          </h3>
          {jobs.length > 0 && (
            <button
              onClick={() => navigate('/garage/jobs')}
              className="flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
            >
              View all jobs <ChevronRight size={13} />
            </button>
          )}
        </div>

        {jobsLoading ? (
          <div className="p-4 space-y-2">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-12 bg-[var(--bg-base)] border border-[var(--border)] rounded-lg animate-pulse" />
            ))}
          </div>
        ) : jobs.length === 0 ? (
          <p className="px-5 py-4 text-sm text-[var(--text-muted)]">No jobs recorded for this customer.</p>
        ) : (
          <div className="divide-y divide-[var(--border)]">
            {jobs.map(job => (
              <button
                key={job.id}
                onClick={() => navigate(`/garage/jobs/${job.id}`)}
                className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-[var(--bg-base)]
                           transition-colors text-left group"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    {job.registration && (
                      <span className="inline-block font-mono font-bold tracking-widest text-xs
                                       bg-yellow-400 text-black px-1.5 py-0.5 rounded">
                        {job.registration}
                      </span>
                    )}
                    <p className="text-sm font-medium text-[var(--text-primary)] group-hover:text-indigo-400 transition-colors truncate">
                      {job.title}
                    </p>
                  </div>
                  {job.date_booked && (
                    <p className="text-xs text-[var(--text-muted)]">
                      {new Date(job.date_booked).toLocaleDateString('en-GB', {
                        day: 'numeric', month: 'short', year: 'numeric',
                      })}
                    </p>
                  )}
                </div>
                <span className={clsx(
                  'shrink-0 px-2 py-0.5 rounded text-xs font-medium',
                  JOB_STATUS_STYLES[job.status] || 'bg-gray-500/15 text-gray-400'
                )}>
                  {JOB_STATUS_LABELS[job.status] || job.status}
                </span>
                <ChevronRight size={14} className="shrink-0 text-[var(--text-muted)] group-hover:text-indigo-400" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
